import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "../App";
import { useAuth } from "../contexts/AuthContext";
import ImageGenerator from "../components/ImageGenerator";
import VideoGenerator from "../components/VideoGenerator";
import { Image, Film, Clock, Download } from "lucide-react";

const API = `/api`;

export default function StudioPage() {
  const { t } = useLanguage();
  const { user, loading, setShowAuthModal } = useAuth();
  const [activeTab, setActiveTab] = useState("image");

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0D0704", display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "64px" }}>
        <div className="loading-ring spin-slow" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ minHeight: "100vh", background: "#0D0704", display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "64px" }}>
        <div data-testid="auth-required-prompt" style={{ textAlign: "center", maxWidth: "440px", padding: "0 1.5rem" }}>
          <span className="font-logo" style={{ fontSize: "2rem", letterSpacing: "0.3em", color: "#D4B878", display: "block", marginBottom: "1.5rem" }}>
            KHATIRA
          </span>
          <h2 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "1.1rem", color: "#EDE0C4", fontWeight: 400, marginBottom: "0.75rem" }}>
            {t("auth_required")}
          </h2>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.88rem", color: "#B8956A", fontWeight: 300, lineHeight: 1.7, marginBottom: "2rem" }}>
            {t("auth_required_desc")}
          </p>
          <button
            data-testid="studio-login-btn"
            className="btn-primary"
            onClick={() => setShowAuthModal(true)}
            style={{ padding: "0.85rem 2.5rem", fontSize: "0.85rem", letterSpacing: "0.18em", textTransform: "uppercase" }}
          >
            {t("auth_login")}
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "image", label: t("tab_image") },
    { id: "video", label: t("tab_video") },
    { id: "my_images", label: t("gallery_images") },
    { id: "my_videos", label: t("gallery_videos") },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#0D0704", paddingTop: "64px" }}>
      <div style={{ background: "#120904", borderBottom: "1px solid rgba(201,168,76,0.1)", position: "sticky", top: "64px", zIndex: 30 }}>
        <div style={{ maxWidth: "860px", margin: "0 auto", padding: "0 1.5rem" }}>
          <div style={{ paddingTop: "1.5rem", paddingBottom: "0.5rem" }}>
            <h1
              className="font-heading"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 600, color: "#EDE0C4", letterSpacing: "-0.01em" }}
              data-testid="studio-title"
            >
              {t("nav_studio")}
            </h1>
          </div>
          <div style={{ display: "flex", gap: "2rem", borderBottom: "1px solid rgba(201,168,76,0.12)", overflowX: "auto" }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                className={`studio-tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "2.5rem 1.5rem 5rem" }}>
        {activeTab === "image" && <ImageGenerator />}
        {activeTab === "video" && <VideoGenerator />}
        {activeTab === "my_images" && <ImageGallery />}
        {activeTab === "my_videos" && <VideoGallery />}
      </div>
    </div>
  );
}

function ImageGallery() {
  const { t } = useLanguage();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/gallery/images`);
        setImages(res.data.images);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <div className="loading-ring spin-slow" style={{ width: 36, height: 36, margin: "0 auto" }} />
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div data-testid="gallery-images-empty" style={{ textAlign: "center", padding: "4rem 1rem" }}>
        <Image size={36} style={{ color: "rgba(201,168,76,0.25)", marginBottom: "1rem" }} />
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.92rem", color: "#B8956A", fontWeight: 300 }}>
          {t("gallery_empty_images")}
        </p>
      </div>
    );
  }

  const handleDownloadImage = async (url, id) => {
    try {
      const res = await fetch(url, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `khatira-image-${id}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {}
  };

  return (
    <div data-testid="gallery-images-grid" className="fade-in-up" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}>
      {images.map((img) => (
        <div
          key={img.id}
          data-testid={`gallery-image-${img.id}`}
          style={{
            background: "#1A1008",
            border: "1px solid rgba(201,168,76,0.12)",
            borderRadius: "2px",
            overflow: "hidden",
            transition: "border-color 0.2s",
          }}
        >
          <img
            src={img.image_url}
            alt={img.prompt}
            style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }}
            loading="lazy"
          />
          <div style={{ padding: "0.75rem" }}>
            <p style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: "0.78rem",
              color: "#B8956A",
              fontWeight: 300,
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              marginBottom: "0.4rem",
            }}>
              {img.prompt}
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <Clock size={11} style={{ color: "rgba(184,149,106,0.4)" }} />
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", color: "rgba(184,149,106,0.4)" }}>
                  {img.created_at ? new Date(img.created_at).toLocaleDateString() : ""}
                </span>
              </div>
              <button
                data-testid={`gallery-dl-btn-image-${img.id}`}
                onClick={() => handleDownloadImage(img.image_url, img.id)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#C9A84C", padding: "0.15rem", display: "flex" }}
                title={t("out_download")}
              >
                <Download size={14} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function VideoGallery() {
  const { t } = useLanguage();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/gallery/videos`);
        setVideos(res.data.videos);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <div className="loading-ring spin-slow" style={{ width: 36, height: 36, margin: "0 auto" }} />
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div data-testid="gallery-videos-empty" style={{ textAlign: "center", padding: "4rem 1rem" }}>
        <Film size={36} style={{ color: "rgba(201,168,76,0.25)", marginBottom: "1rem" }} />
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.92rem", color: "#B8956A", fontWeight: 300 }}>
          {t("gallery_empty_videos")}
        </p>
      </div>
    );
  }

  const handleDownloadVideo = async (url, id) => {
    try {
      const res = await fetch(url, { credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `khatira-video-${id}.mp4`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {}
  };

  return (
    <div data-testid="gallery-videos-grid" className="fade-in-up" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
      {videos.map((vid) => (
        <div
          key={vid.id}
          data-testid={`gallery-video-${vid.id}`}
          style={{
            background: "#1A1008",
            border: "1px solid rgba(201,168,76,0.12)",
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <video
            controls
            style={{ width: "100%", display: "block", maxHeight: "220px", background: "#0A0502" }}
            src={vid.video_url}
          />
          <div style={{ padding: "0.75rem" }}>
            <p style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: "0.78rem",
              color: "#B8956A",
              fontWeight: 300,
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              marginBottom: "0.4rem",
            }}>
              {vid.prompt}
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <Clock size={11} style={{ color: "rgba(184,149,106,0.4)" }} />
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", color: "rgba(184,149,106,0.4)" }}>
                  {vid.created_at ? new Date(vid.created_at).toLocaleDateString() : ""}
                </span>
              </div>
              <button
                data-testid={`gallery-dl-btn-video-${vid.id}`}
                onClick={() => handleDownloadVideo(vid.video_url, vid.id)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#C9A84C", padding: "0.15rem", display: "flex" }}
                title={t("out_download")}
              >
                <Download size={14} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
